import classNames from 'classnames';
import { useAppSelector } from 'hooks/redux';
import React, { useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useMount } from 'react-use';

import { featureFlagsSelectors } from 'app-components/flux/feature-flags';

import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import DiamondIcon from '@mui/icons-material/Diamond';
import SearchIcon from '@mui/icons-material/Search';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import ListItemText from '@mui/material/ListItemText';
import MenuItem from '@mui/material/MenuItem';
import MenuList from '@mui/material/MenuList';
import Paper from '@mui/material/Paper';
import Popper from '@mui/material/Popper/index';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import AppleWalletLogo from 'assets/logos/apple.svg';
import mint from 'assets/logos/mint.png';
import FileImportDialog from 'components/Dialogs/FileImportDialog';
import QPanelButton from 'components/QPanelButton';

import Downshift from 'downshift';
import { Field, Form, Formik } from 'formik';

import { actionHelpers } from 'app-components/flux/core';
import { getLogger, tracker } from 'app-components/utils/core';

import Dump from 'components/Dump';
import FormikTextField from 'components/Formik/TextField';
import InstitutionIcon from 'components/InstitutionIcon';
import QButton from 'components/QButton';
import QTextButton from 'components/QTextButton';

import { accountDiscoveryActions, accountDiscoverySelectors } from 'app-components/flux/account-discovery';
import { institutionsActions, institutionsSelectors, institutionsUtils } from 'app-components/flux/institutions';

import CloudOff from '@mui/icons-material/CloudOff';
import type { accountsTypes } from 'app-components/flux/accounts';
import type { Institution } from 'app-components/flux/institutions/institutionsTypes';
import { isAppleWalletInstitution } from 'data/appleWallet/utils';
import type { List } from 'immutable';
import type { DateTime } from 'luxon';
import { makeStyles } from 'tss-react/mui';

export const useStyles = makeStyles<void, 'iconCircle'>()((theme, _params, classes) => ({
  bankImageCell: {
    display: 'grid',
    alignItems: 'center',
    justifyItems: 'center',
  },
  bottomType: {
    display: 'flex',
    width: '100%',
    justifyContent: 'center',
    gap: theme.spacing(2),
    margin: `${theme.spacing(3)} 0 0`,
    flexDirection: 'column',
    '& .MuiBox-root': {
      gap: 0,
    },
  },
  dropDownContainer: {
    borderRadius: theme.shape.borderRadius * 5,
    boxShadow: theme.shadows[12],
  },
  dropDown: {
    maxHeight: '320px',
    overflowY: 'scroll',
    padding: 0,
    '& div': {
      margin: `${theme.spacing(1)} ${theme.spacing(2)}`,
      borderRadius: theme.shape.borderRadius * 3,
    },
  },
  popularButtonRoot: {
    justifyContent: 'left',
    border: `1px solid ${theme.palette.border.light}`,
    borderRadius: 8,
    '&:hover': {
      color: theme.palette.primary.main,
      border: `1px solid ${theme.palette.primary.main}`,
      backgroundColor: theme.palette.action.hover,
      boxShadow: `0px 0px 0px 4px ${theme.palette.primary.light}`,
    },
    color: theme.palette.text.primary,
    height: '100%',
    fontWeight: 400,
    textTransform: 'none',
    width: '100%',
  },
  popularButtonStartIcon: {
    marginLeft: 0,
  },
  popularCopy: {
    margin: '24px 0 24px 4px',
  },
  pupularButtonIconOnly: {
    backgroundColor: 'transparent',
    border: 'none',
    borderRadius: 7,
    '&:hover': {
      border: '1px solid #4D96EF',
      boxShadow: '0 0 4px #4D96EF',
    },
    height: '100%',
    width: '100%',
  },
  popularGrid: {
    display: 'grid',
    gridTemplateColumns: '166px 166px 166px 166px',
    gridAutoRows: 64,
    gridGap: 16,
    justifyContent: 'center',
  },
  popularGridIconButtonStyle: {
    display: 'grid',
    gridTemplateColumns: '120px 120px 120px 120px 120px',
    gridAutoRows: 64,
    gridGap: 16,
    justifyContent: 'center',
  },
  progress: {
    display: 'block',
    margin: '0 auto',
  },
  root: {
    display: 'flex',
    flex: '1 1 auto',
    flexDirection: 'column',
    padding: '24px',
    position: 'relative',
    overflow: 'hidden',
  },
  searchBar: {
    borderRadius: 12,
  },
  statusContainer: {
    minHeight: '200px',
  },
  popper: {
    zIndex: theme.zIndex.modal,
  },
  cantFindInstitution: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    color: theme.palette.text.secondary,
    maxHeight: 50,
    '& button > p': {
      color: theme.palette.primary.main,
    },
    '& button': {
      verticalAlign: 'initial',
    },
    '&:hover': {
      background: theme.palette.background.paper,
    },
  },
  cantFindDivider: {
    height: 32,
    maxHeight: 32,
  },
  typeSelectorBtnRoot: {
    borderRadius: 12,
    margin: 0,
    flexGrow: 1,
    flexDirection: 'row',
    display: 'flex',
    justifyContent: 'space-between',
    border: `1px solid ${theme.palette.divider}`,
    boxShadow: 'none',
    '&:hover': {
      cursor: 'pointer',
      border: `1px solid ${theme.palette.primary.main}`,
      background: theme.palette.primary.light,
      [`& .${classes.iconCircle}`]: {
        background: theme.palette.background.paper,
      },
    },
    '&:focus': {
      cursor: 'pointer',
      border: `1px solid ${theme.palette.primary.main}`,
    },
  },
  searchIconContainer: {
    alignSelf: 'center',
  },
  buttonText: {
    flexGrow: 1,
    textAlign: 'left',
    display: '-webkit-box',
    overflow: 'hidden',
    'text-overflow': 'ellipsis',
    '-webkit-line-clamp': '2',
    '-webkit-box-orient': 'vertical',
  },
  importButton: {
    textTransform: 'none',
    borderColor: '#1B8381',
    backgroundColor: '#1B8381',
    color: 'white',
    '&:hover': {
      borderColor: '#1B8381',
    },
  },
  importFromMint: {
    display: 'flex',
    flex: 1,
    padding: 16,
    marginTop: 24,
    borderRadius: 6,
    justifyContent: 'space-between',
    alignItems: 'center',
    background: theme.palette.info.light,
  },
  row: {
    display: 'flex',
    alignItems: 'center',
  },
  stacked: {
    display: 'flex',
    flexDirection: 'column',
    paddingLeft: 12,
  },
  endIcon: {
    width: '16px',
    height: '16px',
    marginRight: 4,
  },
  iconCircle: {
    borderRadius: 30,
    background: theme.palette.primary.light,
    width: 48,
    height: 48,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    alignSelf: 'center',
    color: theme.palette.primary.main,
  },
  manualDivider: {
    marginTop: 24,
    marginLeft: -24,
    marginRight: -24,
  },
  newBadge: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    background: theme.palette.primary.light,
    padding: theme.spacing(0.5, 1.5),
    borderRadius: 2,
    margin: '0 !important',
  },
}));

const log = getLogger('InstitutionPicker/index.js');

// @ts-expect-error FIXME TS7006
function extractHostname(url) {
  let hostname;

  // find & remove protocol (http, ftp, etc.) and get hostname
  if (url.indexOf('//') > -1) {
    hostname = url.split('/')[2];
  } else {
    hostname = url.split('/')[0];
  }

  // find & remove any port number
  hostname = hostname.split(':')[0];

  // find & remove any query "?"
  hostname = hostname.split('?')[0];

  // find & remove any beginning www.
  if (hostname.indexOf('www.') === 0) {
    hostname = hostname.slice(4);
  }

  return hostname;
}

// @ts-expect-error FIXME TS7006
function renderSuggestion(params) {
  const { suggestion, key, itemProps, highlightedIndex, selectedItem, newBadgeStyle } = params;
  const isHighlighted = highlightedIndex === key;
  const isSelected = selectedItem && selectedItem.name === suggestion.name;
  const url = suggestion.url ? extractHostname(suggestion.url) : null;
  const isAppleWallet = isAppleWalletInstitution(suggestion);

  return (
    <MenuItem
      // sorry we can't override 'id' - downshift uses it for internal purpose
      {...itemProps}
      key={key}
      selected={isHighlighted}
      component='div'
      style={{
        fontWeight: isSelected ? 500 : 400,
      }}
      sx={(theme) => ({
        border: isAppleWallet && `1px solid ${theme.palette.border.light}`,
      })}
    >
      <Dump obj={suggestion} />
      {isAppleWallet ? (
        <>
          <img src={AppleWalletLogo} alt='Apple Wallet logo' style={{ borderRadius: '50%' }} height={45} width={45} />
          <ListItemText
            id={`institution-suggest-${key}`}
            primary={'Apple Wallet'}
            secondary={'Use Simplifi iOS to connect'}
          />
          <Box className={newBadgeStyle}>
            <AutoAwesomeIcon sx={{ fontSize: 14 }} color='primary' />
            <Typography variant='paragraphRegular' color='primary' ml={1}>
              New
            </Typography>
          </Box>
        </>
      ) : (
        <ListItemText id={`institution-suggest-${key}`} primary={suggestion.name} secondary={url} />
      )}
    </MenuItem>
  );
}

/*  const keyHandle = (highlightedIndex, selectHighlightedItem) => (e) => {
  if (e.keyCode === 9 && highlightedIndex !== null) {
    e.preventDefault();
    selectHighlightedItem();
  }
};  */

const handleSelect = async (
  // @ts-expect-error FIXME TS7006
  selectedInstitution,
  // @ts-expect-error FIXME TS7006
  aggregator,
  // @ts-expect-error FIXME TS7006
  trackingProperties,
  // @ts-expect-error FIXME TS7006
  scope,
  // @ts-expect-error FIXME TS7006
  fixFiMode,
  // @ts-expect-error FIXME TS7006
  dispatch,
  // @ts-expect-error FIXME TS7006
  featureFlags,
) => {
  if (selectedInstitution) {
    const fetchInstitution = actionHelpers.bindPromiseAction(dispatch, institutionsActions.getInstitution);
    const response: any = await fetchInstitution({ id: selectedInstitution.id });
    const institution = response.resource;

    const channel = institutionsUtils.getDefaultChannel(institution, featureFlags);
    dispatch(accountDiscoveryActions.institutionSelected({ institution, channel }, { scope }));

    let institutionId;
    if (institution.ofx) {
      institutionId = institution.ofx?.bid;
    } else if (institution.fds) {
      institutionId = institution.fds?.id;
    } else {
      institutionId = institution.id;
    }
    tracker.track(!fixFiMode ? tracker.events.addFISelect : tracker.events.fixFISelect, {
      ...trackingProperties,
      aggregator: institution?.aggregator || trackingProperties?.aggregator,
      institution_id: institutionId,
      institution_name: institution.name,
      channel: institution.fdp?.channelData?.type,
    });
  }
};

// @ts-expect-error FIXME TS7006
const handleSubmit = (_values, actions) => {
  log.log('Called onSubmit... This does nothing so just resetting form');
  actions.setSubmitting(false);
};

const InstitutionSelectorForm = (props: {
  aggregator: string;
  onAddUnconnectedAccount: (defaultValues?: {
    accountName?: string;
    accountType?: accountsTypes.AccountTypeNode;
    statementDate?: DateTime;
    statementBalance?: string;
  }) => void;
  onAddUnconnectedAsset: () => void;
  onOpenInstitutionRequest: () => void;
  onAddAppleWallet: () => void;
  scope: string;
  trackingProperties: {
    aggregator: string;
    initiator: string;
    mode: string;
  };
  fixFiMode: boolean;
  onFinishLater: () => void;
}) => {
  const {
    aggregator,
    onAddUnconnectedAccount,
    onAddUnconnectedAsset,
    onAddAppleWallet,
    onOpenInstitutionRequest,
    scope,
    trackingProperties,
    fixFiMode,
    onFinishLater,
  } = props;

  const dispatch = useDispatch();
  const { classes } = useStyles();
  const anchorRef = useRef<HTMLElement>();

  const featureFlags: any = useSelector(featureFlagsSelectors.getFeatureFlags);

  const isSubmitting = useSelector((state) => accountDiscoverySelectors.getIsSubmitting(state, { scope }));
  const popularInstitutions = useSelector(institutionsSelectors.getPopularInstitutions);

  const [institutions, setInstitutions] = useState<List<Institution> | undefined>();
  const [lastSearchValue, setLastSearchValue] = useState<string>();

  const [showImportDialog, setShowImportDialog] = useState(false);
  const showImport = () => setShowImportDialog(true);
  const hideImport = () => setShowImportDialog(false);

  const buttonOptions = useMemo(
    () => [
      {
        title: <Typography variant='paragraphMedium'>Track assets</Typography>,
        icon: (
          <div className={classes.iconCircle}>
            <DiamondIcon />
          </div>
        ),
        details: 'Real estate, vehicles, and others',
        onClick: () => onAddUnconnectedAsset(),
        id: 'add_assets',
      },
      {
        title: <Typography variant='paragraphMedium'>Add manual account</Typography>,
        icon: (
          <div className={classes.iconCircle}>
            <AccountBalanceWalletIcon />
          </div>
        ),
        details: 'Cash or manual bank accounts',
        onClick: () => onAddUnconnectedAccount(),
        id: 'add_manually_btn',
      },
    ],
    [classes.iconCircle, onAddUnconnectedAccount, onAddUnconnectedAsset],
  );

  const showMintButtons = useAppSelector((state) => featureFlagsSelectors.getFeatureFlag(state, 'mintImportExtension'));

  useMount(() => {
    dispatch(institutionsActions.getPopularInstitutions(undefined, { axiosConfig: { params: { aggregator } } }));
  });

  const searchForInstitutionsPromise = actionHelpers.bindPromiseAction<undefined, any, List<Institution>[]>(
    dispatch,
    institutionsActions.searchForInstitutions,
  );
  const searchForInstitutions = (input: string) => {
    if (input && input.length > 0) {
      // @ts-expect-error todo - aggregator is not used, but might come back in multi-agg
      searchForInstitutionsPromise({ aggregator, searchString: input })
        .then((resolveInstitutions) => setInstitutions(resolveInstitutions?.[0]))
        .catch((error) => log.error(error));
      setLastSearchValue(input);
    } else {
      // Note: This gets called when the control leaves focus. This can happen clicking away or even by selecting
      // item. In this case, input will not exist. However, on screen, input is still left in control (perhaps
      // interaction with material-ui input isn't setup correctly, not really sure). Also, I don't clear last
      // search term here because when we select an item, then it will always be cleared due to this behavior.
      //
      setInstitutions(undefined);
    }
  };

  // determine logo-style by using first institution in popular list
  const isLogoStyleIcon = useMemo(
    () => popularInstitutions && popularInstitutions.size && popularInstitutions.get(0)?.logoStyle !== 'BUTTON',
    [popularInstitutions],
  );

  const renderPopularInstitutionButtonStyle = (institution: Institution) => (
    <div className={classes.bankImageCell} key={institution.id}>
      <button
        tabIndex={0}
        className={classes.pupularButtonIconOnly}
        onClick={() =>
          handleSelect(
            institution,
            aggregator,
            { ...trackingProperties, selected_by: 'icon' },
            scope,
            fixFiMode,
            dispatch,
            featureFlags,
          )
        }
        type='button'
      >
        {/*
         // @ts-expect-error FIXME TS2322 */}
        <InstitutionIcon aggregator={aggregator} institution={institution} size='md' />
      </button>
    </div>
  );

  const renderPopularInstitution = (institution: Institution) => {
    if (!isLogoStyleIcon) {
      return renderPopularInstitutionButtonStyle(institution);
    }

    const hasFiIssue = featureFlags.get('fiStatus') && institution.fiIssue;

    return (
      <div className={classes.bankImageCell} key={institution.id}>
        <Dump obj={institution} />
        <Button
          classes={{
            root: classes.popularButtonRoot,
            startIcon: classes.popularButtonStartIcon,
          }}
          onClick={() =>
            handleSelect(
              institution,
              aggregator,
              { ...trackingProperties, selected_by: 'icon' },
              scope,
              fixFiMode,
              dispatch,
              featureFlags,
            )
          }
          // @ts-expect-error FIXME TS2322
          startIcon={<InstitutionIcon aggregator={aggregator} institution={institution} size='sm' />}
          endIcon={hasFiIssue ? <CloudOff className={classes.endIcon} /> : null}
        >
          <Tooltip title={institution.name}>
            <div className={classes.buttonText}>{institution.name}</div>
          </Tooltip>
        </Button>
      </div>
    );
  };

  const renderMoveFromMint = () => (
    <div className={classes.importFromMint}>
      <div className={classes.row}>
        <img src={mint} alt='' />
        <div className={classes.stacked}>
          <Typography variant='bodyMedium'>Coming from Mint?</Typography>
          <Typography variant='bodyRegular'>Start here to move your data over.</Typography>
        </div>
      </div>
      <div>
        <QButton variant='outlined' className={classes.importButton} onClick={showImport}>
          Import From Mint
        </QButton>
      </div>
    </div>
  );

  return (
    <>
      {showImportDialog && (
        <FileImportDialog
          isGSFImport
          onFinishLater={onFinishLater}
          open={showImportDialog}
          onHandleClose={hideImport}
        />
      )}

      {isSubmitting && (
        <div className={classes.statusContainer}>
          <CircularProgress size={100} className={classes.progress} />
        </div>
      )}
      <Formik initialValues={{}} onSubmit={(values, actions) => handleSubmit(values, actions)}>
        {(formikBag) => {
          const { handleSubmit: formikHandleSubmit } = formikBag;
          return (
            <Form onSubmit={formikHandleSubmit} className={classes.root}>
              <Downshift
                initialHighlightedIndex={0}
                defaultHighlightedIndex={0}
                itemToString={(item) => (item ? item.label : '')}
                onInputValueChange={(input) => searchForInstitutions(input)}
                onSelect={(item) => {
                  if (isAppleWalletInstitution(item)) {
                    onAddAppleWallet();
                    return;
                  }
                  handleSelect(
                    item,
                    aggregator,
                    { ...trackingProperties, selected_by: 'search', last_search_value: lastSearchValue },
                    scope,
                    fixFiMode,
                    dispatch,
                    featureFlags,
                  );
                }}
              >
                {({
                  getInputProps,
                  getItemProps,
                  reset,
                  selectedItem,
                  highlightedIndex,
                  /*  selectHighlightedItem,  */ isOpen,
                  getMenuProps,
                  getRootProps,
                }) => {
                  const { onBlur, onChange, value, ...inputProps } = getInputProps();
                  return (
                    <div {...getRootProps(undefined, { suppressRefError: true })}>
                      <Field
                        inputRef={anchorRef}
                        component={FormikTextField}
                        onFieldBlur={onBlur}
                        onFieldChange={onChange}
                        //  onKeyDown={keyHandle(highlightedIndex, selectHighlightedItem)}
                        fullWidth
                        margin='dense'
                        variant='filled'
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position='end' className={classes.searchIconContainer}>
                              <IconButton aria-label='Clear search' onClick={() => reset()} size='large' tabIndex={-1}>
                                <SearchIcon />
                              </IconButton>
                            </InputAdornment>
                          ),
                          ...inputProps,
                          inputProps: {
                            autoCorrect: 'off',
                            id: 'bankSearch',
                            autoFocus: true,
                          },
                        }}
                        label='Search all financial institutions'
                        name='institution'
                        className={classes.searchBar}
                      />
                      <Popper
                        open={isOpen && typeof value === 'string' && value.length > 0}
                        anchorEl={anchorRef.current}
                        placement='bottom-start'
                        className={classes.popper}
                        style={{
                          ...(anchorRef.current ? { width: anchorRef.current.parentElement?.clientWidth } : {}),
                        }}
                      >
                        <Paper className={classes.dropDownContainer} square>
                          <MenuList className={classes.dropDown} {...getMenuProps({}, { suppressRefError: true })}>
                            {institutions?.size ? (
                              institutions.map((institution, key) =>
                                renderSuggestion({
                                  suggestion: institution,
                                  key,
                                  itemProps: getItemProps({ item: institution }),
                                  highlightedIndex,
                                  selectedItem,
                                  newBadgeStyle: classes.newBadge,
                                }),
                              )
                            ) : (
                              <MenuItem id='no-results' component='div'>
                                <ListItemText
                                  primary={
                                    <>
                                      No matching results for <strong>{value}</strong>
                                    </>
                                  }
                                />
                              </MenuItem>
                            )}
                            <Divider orientation={'horizontal'} variant={'fullWidth'} />
                            <MenuItem component={'div'} className={classes.cantFindInstitution}>
                              <Box flex={3} display={'flex'} alignItems={'center'} justifyContent={'flex-start'}>
                                <Typography color={'inherit'} variant='paragraphRegular' component={'span'}>
                                  Can&apos;t find your institution?&nbsp;
                                  <QTextButton
                                    title={'Send request'}
                                    variant='paragraphRegular'
                                    onClick={() => onOpenInstitutionRequest()}
                                  />
                                  {' or '}
                                  {
                                    <QTextButton
                                      title={'get help'}
                                      variant='paragraphRegular'
                                      id={'intercom-launcher'}
                                    />
                                  }
                                  .
                                </Typography>
                              </Box>
                              <Divider
                                orientation={'vertical'}
                                flexItem
                                className={classes.cantFindDivider}
                                variant={'middle'}
                              />
                              <Box flex={1} paddingLeft={2} paddingRight={2}>
                                <QButton onClick={onAddUnconnectedAccount}>add manual account</QButton>
                              </Box>
                            </MenuItem>
                          </MenuList>
                        </Paper>
                      </Popper>
                    </div>
                  );
                }}
              </Downshift>
              <Typography variant='bodyBold' className={classes.popularCopy}>
                Popular financial institutions
              </Typography>
              {popularInstitutions && popularInstitutions.size && (
                <div className={classNames(isLogoStyleIcon ? classes.popularGrid : classes.popularGridIconButtonStyle)}>
                  {popularInstitutions.map(renderPopularInstitution)}
                </div>
              )}
              <>
                <Divider className={classes.manualDivider} />
                {trackingProperties.mode === 'ADD-FI' && showMintButtons && renderMoveFromMint()}
                <div className={classes.bottomType} style={{ flexDirection: 'row' }}>
                  {buttonOptions.map((items) => (
                    <QPanelButton
                      className={classes.typeSelectorBtnRoot}
                      id={items.id}
                      onClick={items.onClick}
                      key={items.id}
                      title={items.title}
                      startIcon={items.icon}
                      subtitle={items.details}
                      textMarginRight={0}
                    />
                  ))}
                </div>
              </>
            </Form>
          );
        }}
      </Formik>
    </>
  );
};

export default InstitutionSelectorForm;
